/**
 * Customer Analytics Page
 *
 * Customer portfolio analysis with segmentation and trends.
 */

import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useCustomerAnalytics, useCurrentPeriod } from "../hooks/useCustomerAnalytics";
import { PeriodSelector } from "../components/sales/PeriodSelector";
import { CustomerTable } from "../components/sales/CustomerTable";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Users, UserPlus, UserMinus, Star, ArrowLeft } from "lucide-react";
import { t } from "@/shared/lib/th-translations";

export default function CustomerAnalytics() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentPeriod = useCurrentPeriod();
  const [selectedPeriod, setSelectedPeriod] = useState(currentPeriod);

  const { data, isLoading, error } = useCustomerAnalytics({
    salespersonId: id,
    period: selectedPeriod,
    limit: 100
  });

  if (!id) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">{t("customerAnalytics.messages.noSalespersonSelected")}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-red-600">Error loading customer data: {error instanceof Error ? error.message : 'Unknown error'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">{t("customerAnalytics.messages.noData")}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header with Breadcrumb */}
      <div className="space-y-4">
        <Button
          variant="ghost"
          onClick={() => navigate(`/analytics/salespersons/${id}`)}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Salesperson Details
        </Button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">{t("customerAnalytics.title")}</h1>
            <p className="text-muted-foreground">Salesperson ID: {id}</p>
          </div>
          <PeriodSelector value={selectedPeriod} onChange={setSelectedPeriod} />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Customers</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.totalCustomers}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">New Customers</CardTitle>
            <UserPlus className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{data.newCustomers}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Churned Customers</CardTitle>
            <UserMinus className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{data.churnedCustomers}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">High Impact</CardTitle>
            <Star className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {data.customers.filter(c => c.flags.isHighImpact).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Customer Table */}
      <CustomerTable customers={data.customers} />
    </div>
  );
}
